"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, Loader2, X } from "lucide-react";
import posthog from "posthog-js";

import { Button } from "@/components/ui/Button";

// VOR-104 — modal de "Quero acesso antecipado".
// Antes vivia em /zenthor (page dedicada com hero + features + form).
// Bea pediu pra ser modal com form simples e deletar a page.
//
// Submete pra POST /api/waitlist (mesma rota anterior). Estado de
// sucesso fica inline no modal (substitui /zenthor/obrigado).

const inputClass =
  "w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-ink placeholder:text-mist/50 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20";

type Status = "idle" | "submitting" | "success" | "error";

type WaitlistModalProps = {
  open: boolean;
  onClose: () => void;
  source?: string;
};

export function WaitlistModal({ open, onClose, source = "modal" }: WaitlistModalProps) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  // VOR-128: consent checkbox removido — Bea pediu remoção. consent default
  // true porque o ato de submeter o form já é manifestação de interesse;
  // a política continua linkada via /privacidade no footer.
  const consent = true;
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Reseta estado ao fechar pra próxima abertura começar limpa.
  useEffect(() => {
    if (!open) {
      setStatus("idle");
      setErrorMessage("");
    }
  }, [open]);

  // Esc fecha o modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Foco no botão de fechar quando abre.
  useEffect(() => {
    if (open) closeButtonRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, firstName, consent, website }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg =
          typeof data?.error === "string"
            ? data.error
            : "Não foi possível registrar agora. Tente novamente em alguns instantes.";
        throw new Error(msg);
      }

      if (process.env.NEXT_PUBLIC_POSTHOG_TOKEN) {
        posthog.capture("zenthor_waitlist_joined", { source });
      }

      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Erro inesperado.");
    }
  };

  const isSubmitting = status === "submitting";
  const isSuccess = status === "success";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="waitlist-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-canvas p-6 shadow-2xl sm:p-8">
        <button
          ref={closeButtonRef}
          type="button"
          aria-label="Fechar"
          onClick={onClose}
          className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-mist transition hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>

        {isSuccess ? (
          <div className="text-center">
            <h2
              id="waitlist-title"
              className="font-display text-2xl font-medium text-white"
            >
              Você está na lista
            </h2>
            {/* VOR-128: copy do feedback de sucesso atualizada (sem ponto final) */}
            <p className="mt-3 text-sm leading-7 text-mist">
              Ficamos felizes pelo seu interesse. Você vai receber atualizações do Zenthor por e-mail
            </p>
            <div className="mt-6">
              <Button type="button" variant="ghost" onClick={onClose}>
                Fechar
              </Button>
            </div>
          </div>
        ) : (
          <>
            <h2
              id="waitlist-title"
              className="font-display text-2xl font-medium text-white"
            >
              Quero acesso antecipado
            </h2>
            {/* VOR-128: subtitle reescrito (sem ponto final) */}
            <p className="mt-2 text-sm leading-6 text-mist">
              A gente avisa assim que o Zenthor estiver disponível
            </p>

            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div>
                {/* VOR-128: label "Como podemos te chamar" → "Nome", placeholder removido */}
                <label
                  htmlFor="waitlist-firstName"
                  className="mb-2 block text-sm font-medium text-ink"
                >
                  Nome
                </label>
                <input
                  id="waitlist-firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClass}
                  autoComplete="given-name"
                  maxLength={80}
                />
              </div>

              <div>
                {/* VOR-128: asterisco * removido do label */}
                <label
                  htmlFor="waitlist-email"
                  className="mb-2 block text-sm font-medium text-ink"
                >
                  E-mail
                </label>
                <input
                  id="waitlist-email"
                  type="email"
                  required
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  autoComplete="email"
                  maxLength={254}
                />
              </div>

              <div className="hidden" aria-hidden="true">
                <label htmlFor="waitlist-website">Não preencha este campo</label>
                <input
                  id="waitlist-website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              {/* VOR-128: checkbox de consentimento removido. Política continua linkada via footer. */}

              <div role="status" aria-live="polite" className="min-h-[1.25rem]">
                {status === "error" ? (
                  <p className="text-sm text-red-300">{errorMessage}</p>
                ) : null}
              </div>

              {/* VOR-128: ícone movido pra direita do botão (antes era esquerda com <Send>) */}
              <Button type="submit" className="w-full gap-2">
                {isSubmitting ? "Enviando…" : "Entrar na lista"}
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                )}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
